<script setup lang="ts">
import {computed, ref} from 'vue';
import {SessionState, type Homework, type Session} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {useI18n} from 'vue-i18n';
import InterventionLandingSession from '@/vue/organisms/intervention-landing/intervention-landing-session.vue';
import HomeworkCard from '@/vue/molecules/session-summary/homework-card.vue';
import HomeworkModal from '@/vue/organisms/homework-modal.vue';
import {useDragToScroll} from '@/ts/composables/pure/use-drag-to-scroll';

const {t: $t} = useI18n();

const props = defineProps<{ sessions: Session[] }>();

// Use the drag-to-scroll composable
const {scrollAreaRef, onMouseDown} = useDragToScroll();

type CarouselItem =
  | { type: 'session'; session: Session; index: number }
  | { type: 'homework'; homework: Homework; sessionId: UuidDTO };

const carouselItems = computed<CarouselItem[]>(() => {
  const items: CarouselItem[] = [];
  for (let i = 0; i < props.sessions.length; i++) {
    const session = props.sessions[i];
    items.push({type: 'session', session, index: i});

    // After a completed session, check if the next session has pending homework
    if (session.state === SessionState.COMPLETED) {
      const nextSession = props.sessions[i + 1];
      const hw = nextSession?.homework[0];
      if (hw && !hw.completedDate) {
        items.push({type: 'homework', homework: hw, sessionId: nextSession.id});
      }
    }
  }
  return items;
});

const homeworkOpen = ref(false);
const homeworkSessionId = ref<UuidDTO>();

function openHomework(sessionId: UuidDTO): void {
  homeworkSessionId.value = sessionId;
  homeworkOpen.value = true;
}
</script>

<template>
  <div class="intervention-landing-sessions">
    <h3 class="title">
      {{ $t('sessions') }}
    </h3>
    <homework-modal v-if="homeworkSessionId" v-model="homeworkOpen" :homework-session-id="homeworkSessionId"/>
    <q-scroll-area ref="scrollAreaRef" class="scroll-area" style="max-width: 100%;">
      <div
        class="row no-wrap sessions sessions-grab"
        @mousedown="onMouseDown"
      >
        <div v-for="item in carouselItems" :key="item.type === 'session' ? item.session.id : 'hw-' + item.sessionId" class="session">
          <intervention-landing-session
            v-if="item.type === 'session'"
            :index="item.index"
            :session="item.session"
          />
          <homework-card
            v-else
            :homework="item.homework"
            variant="carousel"
            @open-homework="openHomework(item.sessionId)"
          />
        </div>
      </div>
    </q-scroll-area>
  </div>
</template>

<style scoped lang="scss">
.scroll-area {
  // card height (spacing-13) + padding + scrollbar margin
  height: calc(var(--Spacing-spacing-13) + var(--Spacing-spacing-5) + var(--Spacing-spacing-1));

  @include screen-md {
    height: calc(var(--Spacing-spacing-13) + var(--Spacing-spacing-5));
  }
}

.title {
  @include font-h3-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

.sessions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  padding: 6px 0;
}

.sessions-grab {
  cursor: grab;
  user-select: none;
}
</style>
